import {
  Box,
  Button,
  Card,
  Center,
  Container,
  Flex,
  Grid,
  Image,
  Loader,
  SimpleGrid,
  Text,
  TextInput,
  createStyles,
  rem,
} from "@mantine/core";
import { Dropzone, MIME_TYPES } from "@mantine/dropzone";
import { useForm } from "@mantine/form";
import { IconCamera } from "@tabler/icons-react";
import { useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  updateProfile,
  updateProfilePhoto,
} from "../../../../services/profile";
import { useNavigate } from "react-router-dom";
import { useDisclosure } from "@mantine/hooks";
import VerifyPassword from "../../../../components/VerifyPassword";
import { showError, showSuccess } from "../../../../utils";
import { uploadFileToS3Api } from "../../../../utils/PresignedUrlFunction";

const useStyles = createStyles((theme) => ({
  wrapper: {
    marginTop: 40,
    marginBottom: 40,
    borderWidth: 1,
    borderRadius: 15,
    borderStyle: "solid",
    borderColor: "#FFDEABEE",
    display: "flex",
    flexDirection: "row",
    "& .mantine-Container-root": {
      padding: "0px !important",
    },
  },
  camera: {
    position: "absolute",
    marginTop: 75,
    marginLeft: 75,
    background: "#263238",
    borderRadius: "50%",
    padding: 5,
    [`&:hover`]: {
      cursor: "pointer",
    },
  },
  box: {
    backgroundColor: "#FFF",
    borderRadius: 12,
    padding: "30px 8px",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    "@media (max-width :40em)": {
      maxWidth: "100%",
      width: "100%",
    },
  },
  btn: {
    [`&:hover`]: {
      cursor: "pointer",
    },
  },
  inputIcon: {
    borderRightWidth: 1,
    borderRigthColor: "#CBCBCB",
    borderRightStyle: "solid",
    paddingRight: 5,
  },
  changepwd: {
    position: "absolute",
    color: "#FFBA45",
    fontFamily: "Inter",
    fontSize: rem(14),
    marginLeft: "auto",
    textDecoration: "underline",
    paddingBottom: 10,
    marginTop: 9,
  },
  button: { width: 122, fontFamily: "Inter", fontSize: 16, fontWeight: 500 },
  drop: {
    display: "none",
    "& .mantine-Image-image": {
      border: "2px solid green",
    },
  },
  wrapperContianer: {
    marginBottom: 20,
    "@media (max-width :40em)": {
      display: "flex",
      flexDirection: "column",
    },
  },
  inputContainer: {
    padding: "23px 37px 0 38px",
    flexDirection: "column",
    gap: 16,
    background: "#FFF",
    borderRadius: 12,
    boxShadow: "0 1px 2px 0 rgba(16, 24, 40, 0.05)",
  },
  dropImage: {
    img: {
      borderRadius: "50%",
    },
  },
  inputField: {
    "& .mantine-InputWrapper-label": {
      fontSize: 14,
      fontWeight: 500,
      fontFamily: "Inter",
      color: "#344054",
      marginBottom: 8,
    },
    "& .mantine-Input-wrapper": {
      height: 48,
      borderRadius: 8,
      border: "1px solid #CBD5E1",
      background: "transparent",
      transition: "border-color 0.2s ease",

      "& .mantine-Select-rightSection svg": {
        color: "#94A3B8",
      },

      "& input": {
        height: "100%",
        border: "none",
        borderRadius: 8,
        padding: "0px 12px",
        background: "transparent",
        minHeight: "unset",
        fontSize: 14,
        fontWeight: 400,
        fontFamily: "Inter",
        color: "#0F172A",

        "&::placeholder": {
          color: "#A3A3A3",
        },

        "&:focus": {
          outline: "none",
        },
      },
    },

    "& .mantine-Select-wrapper": {
      padding: 0,
      "& .mantine-Select-input": {
        paddingLeft: 12,
        paddingRight: 36,
        height: 48,
        fontSize: 16,
        fontWeight: 400,
        fontFamily: "Inter",
        color: "#0F172A",

        "&::placeholder": {
          color: "#A3A3A3",
        },
      },
    },
  },
  btnsRow: {
    gap: 12,
    borderTop: "1px solid #E5E5E5",
    marginTop: 22,
    padding: "18px 0px",
  },
  cancelBtn: {
    padding: "0px 20px",
    borderRadius: "8px",
    border: "1px solid #F5F5F5",
    backgroundColor: "#F5F5F5",
    height: 60,
    flex: 1,
    boxShadow: "0 1px 2px 0 rgba(16, 24, 40, 0.05)",
    fontSize: 18,
    fontWeight: 600,
    fontFamily: "Inter",
    color: "#262626",
    ":hover": {
      backgroundColor: "#F9FAFB",
      opacity: 0.8,
    },
  },
  submitBtn: {
    padding: "0px 20px",
    borderRadius: "8px",
    border: "1px solid #FFAF4A",
    backgroundColor: "#FFAF4A",
    height: 60,
    boxShadow: "0 1px 2px 0 rgba(16, 24, 40, 0.05)",
    fontSize: 18,
    fontWeight: 600,
    fontFamily: "Inter",
    flex: 1,
    color: "#262626",
    ":hover": {
      backgroundColor: "#FFAF4A",
      opacity: 0.8,
    },
  },
  hdng: {
    fontFamily: "Inter",
    fontSize: 24,
    fontWeight: 700,
    color: "#000",
    marginBottom: 15,
  },
}));

const MyProfile = (props) => {
  const { classes } = useStyles();
  const [opened, { open, close }] = useDisclosure(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [selectedPhotoLoading, setSelectedPhotoLoading] = useState(false);
  const { profile, saveProfileLoading } = useSelector(
    (state) => state?.profile,
  );
  const [objForUpdate, setObjForUpdate] = useState(null);
  const openRef = useRef();
  const navigate = useNavigate();

  const form = useForm({
    initialValues: {
      email: profile?.user?.email,
      name: profile?.user?.name,
    },
    validate: {
      name: (value) => (value ? null : "Required"),
      email: (value) => (/^\S+@\S+$/.test(value) ? null : "Invalid email"),
      // password: (value) => (value ? null : "Required")
    },
  });
  const onFinish = async (values) => {
    let obj = {
      name: values?.name,
    };
    try {
      if (selectedPhoto) {
        const response = await updateProfilePhoto({ files: selectedPhoto });
        showSuccess(response?.message);
      }

      if (values?.email != profile?.user?.email) {
        open();
        obj.email = values?.email;
        setObjForUpdate({
          name: values?.name,
          oldEmail: profile?.user?.email,
          newEmail: values?.email,
        });
      } else {
        updateProfile(obj, navigate);
      }
    } catch (error) {
      console.error("Upload failed", error);
      showError(error?.message || "Upload failed");
    }
  };

  // const onChangePhoto = async (files) => {
  //   if (!files?.length) return;
  //   const file = files[0];

  //   try {
  //     // upload to s3
  //     const result = await uploadFileToS3Api({ file });
  //     console.log("🚀 ~ onChangePhoto ~ result:", result);

  //     // upload to db
  //     const response = await updateProfilePhoto({ files: result });
  //     console.log("🚀 ~ onChangePhoto ~ response:", response);

  //     showSuccess(response?.message);
  //   } catch (error) {
  //     console.error("Upload failed", error);
  //     showError(error?.message || "Upload failed");
  //   }
  // };

  const handlePhotoChange = async (files) => {
    if (!files?.length) return;
    const file = files[0];
    try {
      setSelectedPhotoLoading(true);
      const result = await uploadFileToS3Api({ file });
      setSelectedPhoto(result);
    } catch (error) {
      console.error("Upload failed", error);
      showError(error?.message || "Upload failed");
    } finally {
      setSelectedPhotoLoading(false);
    }
  };

  // const onChangePhoto = (files) => {
  //   if (files?.length > 0) {
  //     const formData = new FormData();
  //     formData.append("file", files[0]);
  //     updateProfilePhoto(formData);
  //   }
  // };

  return (
    <>
      <form
        onSubmit={form?.onSubmit((values) => onFinish(values))}
        className={classes.profileForm}
      >
        <p className={classes.hdng}>Profile details</p>
        <Grid columns={100} className={classes.wrapperContianer}>
          <Grid.Col span={30}>
            <Box className={classes.box}>
            <Center>
              <Dropzone
                onDrop={(files) => handlePhotoChange(files)}
                className={classes.drop}
                openRef={openRef}
                accept={MIME_TYPES.jpeg | MIME_TYPES.png}
              ></Dropzone>

              {selectedPhotoLoading && !selectedPhoto ? (
                <Flex
                  h={111}
                  w={111}
                  align="center"
                  justify="center"
                  sx={{ borderRadius: "50%" }}
                >
                  <Loader color="#FFBA45" />
                </Flex>
              ) : (
                <Image
                  src={
                    selectedPhoto?.uploadURL ||
                    profile?.user?.photo ||
                    "/assets/images/avatar.png"
                  }
                  alt=""
                  width={111}
                  height={111}
                  onClick={() => openRef.current?.()}
                  className={classes.dropImage}
                />
              )}
              <IconCamera
                color="white"
                className={classes.camera}
                onClick={() => openRef.current?.()}
              />
            </Center>
            <Box sx={{ marginTop: 20 }}>
              <Text ff={"Inter"} fw={700} fz={24}>
                {profile?.user?.name}
              </Text>
              <Text ff={"Inter"} fw={600} fz={18} c={"#979797"} mt={10}>
                {profile?.user?.position}
              </Text>
            </Box>
            </Box>
          </Grid.Col>
          <Grid.Col span={"auto"}>
            <Flex className={classes.inputContainer}>
              <TextInput
                label={"Name"}
                className={classes.inputField}
                {...form.getInputProps("name")}
              />
              <TextInput
                label={"Email"}
                className={classes.inputField}
                {...form.getInputProps("email")}
                // disabled
              />
              <Flex className={classes.btnsRow}>
                <Button
                  className={classes.cancelBtn}
                  onClick={() => {
                    setSelectedPhoto(null);
                    navigate("/home/dashboard");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className={classes.submitBtn}
                  loading={saveProfileLoading}
                >
                  Save
                </Button>
              </Flex>
            </Flex>
          </Grid.Col>
        </Grid>
      </form>
      <VerifyPassword
        onclose={close}
        opened={opened}
        data={profile?.user}
        objForUpdate={objForUpdate}
      />
    </>
  );
};

export default MyProfile;
